import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError } from '../lib/api';
import { Button } from '../components/ui/Button';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { PageHeader } from '../components/ui/PageHeader';
import { requireAccess } from '../lib/guards';
import {
  zeiterfassungApi,
  type Antrag,
  type AntragStatus,
  type AntragTyp,
} from '../lib/zeiterfassung-api';
import { formatGermanDateFromTimestamp } from '../lib/date-format';

export const Route = createFileRoute('/zeiterfassung/admin')({
  beforeLoad: () =>
    // Probe approve-Endpoint? Es gibt keine Lese-Berechtigung-Probe; wir nutzen
    // einen Read-on-all-User-Endpoint (Liste mit user_id-Filter), der read:alle
    // braucht. Das schliesst Aussendienst/Reinigung sauber via 403 aus.
    requireAccess('/api/v1/zeiterfassung/antraege?userId=00000000-0000-0000-0000-000000000000'),
  component: ZeiterfassungAdminPage,
});

const TYP_LABEL: Record<AntragTyp, string> = {
  urlaub: 'Urlaub',
  abwesenheit: 'Abwesenheit',
  korrektur: 'Korrektur',
  manueller_eintrag: 'Manueller Eintrag',
};

const STATUS_LABEL: Record<AntragStatus, string> = {
  ausstehend: 'Ausstehend',
  genehmigt: 'Genehmigt',
  abgelehnt: 'Abgelehnt',
};

const STATUS_FARBE: Record<AntragStatus, string> = {
  ausstehend: 'bg-amber-100 text-amber-800 border-amber-200',
  genehmigt: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  abgelehnt: 'bg-red-100 text-red-800 border-red-200',
};

function ZeiterfassungAdminPage(): JSX.Element {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<AntragStatus | 'alle'>('ausstehend');

  const antraegeQ = useQuery({
    queryKey: ['zeiterfassung', 'admin', 'antraege', filter],
    queryFn: () => zeiterfassungApi.antraegeListe(filter === 'alle' ? {} : { status: filter }),
    refetchInterval: 30_000,
  });

  const genehmigen = useMutation({
    mutationFn: (id: string) => zeiterfassungApi.antragGenehmigen(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['zeiterfassung'] });
    },
  });

  const ablehnen = useMutation({
    mutationFn: (input: { id: string; grund: string }) =>
      zeiterfassungApi.antragAblehnen(input.id, input.grund),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['zeiterfassung'] });
    },
  });

  const items = antraegeQ.data?.items ?? [];
  const counts = items.reduce<Record<string, number>>((acc, a) => {
    acc[a.status] = (acc[a.status] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <PageHeader
        title="Zeiterfassung — Anträge"
        description="Eingegangene Anträge prüfen, genehmigen oder ablehnen."
      />

      <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="font-medium text-slate-700">Filter:</span>
          <Select
            value={filter}
            onChange={(e) => setFilter(e.target.value as AntragStatus | 'alle')}
            className="w-44"
          >
            <option value="ausstehend">Ausstehend ({counts['ausstehend'] ?? 0})</option>
            <option value="genehmigt">Genehmigt</option>
            <option value="abgelehnt">Abgelehnt</option>
            <option value="alle">Alle</option>
          </Select>
        </div>
      </div>

      {antraegeQ.isError ? <ErrorBanner error={antraegeQ.error as ApiError} /> : null}
      {genehmigen.isError ? <ErrorBanner error={genehmigen.error as ApiError} /> : null}
      {ablehnen.isError ? <ErrorBanner error={ablehnen.error as ApiError} /> : null}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">Eingang</th>
              <th className="px-3 py-2 text-left">User</th>
              <th className="px-3 py-2 text-left">Typ</th>
              <th className="px-3 py-2 text-left">Details</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Aktion</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-sm">
            {items.length === 0 ? (
              <tr>
                <td className="px-3 py-6 text-center text-slate-500" colSpan={6}>
                  {antraegeQ.isLoading ? 'lädt …' : 'Keine Anträge.'}
                </td>
              </tr>
            ) : (
              items.map((a) => (
                <AntragRow
                  key={a.id}
                  antrag={a}
                  pending={genehmigen.isPending || ablehnen.isPending}
                  onGenehmigen={() => genehmigen.mutate(a.id)}
                  onAblehnen={(grund) => ablehnen.mutate({ id: a.id, grund })}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AntragRow({
  antrag,
  pending,
  onGenehmigen,
  onAblehnen,
}: {
  antrag: Antrag;
  pending: boolean;
  onGenehmigen: () => void;
  onAblehnen: (grund: string) => void;
}): JSX.Element {
  const [reasonOpen, setReasonOpen] = useState(false);
  const [grund, setGrund] = useState('');

  const offen = antrag.status === 'ausstehend';
  const details = formatPayload(antrag);

  return (
    <tr className="hover:bg-slate-50">
      <td className="px-3 py-2 font-mono text-xs text-slate-600">
        {formatGermanDateFromTimestamp(antrag.createdAt)}
      </td>
      <td className="px-3 py-2 font-mono text-xs text-slate-600">{antrag.userId.slice(0, 8)}…</td>
      <td className="px-3 py-2 text-slate-700">{TYP_LABEL[antrag.typ]}</td>
      <td className="px-3 py-2 text-xs text-slate-600">{details}</td>
      <td className="px-3 py-2">
        <span
          className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${STATUS_FARBE[antrag.status]}`}
        >
          {STATUS_LABEL[antrag.status]}
        </span>
      </td>
      <td className="px-3 py-2 text-right">
        {offen ? (
          reasonOpen ? (
            <div className="flex flex-col items-end gap-2">
              <Input
                value={grund}
                onChange={(e) => setGrund(e.target.value)}
                placeholder="Begründung (optional)"
                className="w-56"
              />
              <div className="flex gap-1">
                <Button
                  type="button"
                  className="bg-red-600 hover:bg-red-700"
                  disabled={pending}
                  onClick={() => {
                    onAblehnen(grund || '');
                    setReasonOpen(false);
                    setGrund('');
                  }}
                >
                  Ablehnen
                </Button>
                <Button
                  type="button"
                  className="bg-slate-500 hover:bg-slate-600"
                  onClick={() => setReasonOpen(false)}
                >
                  Abbrechen
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex justify-end gap-1">
              <Button
                type="button"
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={pending}
                onClick={onGenehmigen}
              >
                Genehmigen
              </Button>
              <Button
                type="button"
                className="bg-slate-500 hover:bg-slate-600"
                onClick={() => setReasonOpen(true)}
              >
                Ablehnen
              </Button>
            </div>
          )
        ) : (
          <span className="text-xs text-slate-400">—</span>
        )}
      </td>
    </tr>
  );
}

function formatPayload(antrag: Antrag): string {
  const p = antrag.payload as Record<string, string | undefined>;
  if (antrag.typ === 'urlaub') {
    return `${p['startDatum'] ?? '?'} → ${p['endDatum'] ?? '?'}${p['notiz'] ? ` (${p['notiz']})` : ''}`;
  }
  if (antrag.typ === 'abwesenheit') {
    return `${p['abwesenheitTyp'] ?? '?'}: ${p['startDatum'] ?? '?'} → ${p['endDatum'] ?? '?'}`;
  }
  // korrektur / manueller_eintrag
  return `${p['datum'] ?? '?'} ${p['typ'] ?? '?'} um ${formatTime(p['zeitpunkt'])}${p['begruendung'] ? ` — ${p['begruendung']}` : ''}`;
}

function formatTime(iso: string | undefined): string {
  if (!iso) return '?';
  return new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}
